/**
 * ONE PIECE TCG - Complete Game Configuration
 *
 * This is the ONLY file you need to modify for One Piece-specific settings.
 * Everything about the game is configured here.
 */

import { OPHandler } from "./handler";
import {
  getCardData,
  getAACardIdData,
  getCardImage,
  getAAFromCardId,
} from "@/lib/games/onePiece/getCardDataLocal";
import { CDN } from "@/lib/cdn";
import { GAME_DOMAINS } from "@/games/domains";

export const onePieceGame = {
  // ============================================================================
  // BASIC INFO
  // ============================================================================
  id: "one-piece" as const,
  name: "One Piece TCG",
  abbreviation: "OPTCG",
  gameId: 68, // Database game ID (TCGplayer category ID)

  // ============================================================================
  // SEO & METADATA
  // ============================================================================
  seo: {
    title: "One Piece TCG Meta Decklists & Card DB",
    description:
      "Browse winning One Piece TCG decklists from championship events, regional qualifiers, and flagship tournaments. Find competitive deck builds, card strategies, and meta analysis for OP17, OP16, and all OPTCG formats. Updated daily with top tournament results to help you dominate the competitive scene.",
  },

  // ============================================================================
  // GAME MECHANICS
  // ============================================================================
  mechanics: {
    hasLeader: true,
    hasLeaderDistribution: true,
    hasPieChart: true,
    hasFormatReleaseDates: true,
    maxCopiesDefault: 4,
    hasLifeProperty: true,
    leaderType: "Leader",
  },

  tcgplayer: {
    categoryId: 68,
    productLine: "One Piece Card Game",
  },

  /** One Piece is the root game on gumgum.gg, so its routes are un-prefixed. */
  routePrefix: "",

  // ============================================================================
  // FORMATS & SETS
  // ============================================================================
  // East and West regions are now unified under global
  // Sets must be uppercase to match database
  formats: {
    global: ["OP17", "OP16"],
  },

  /** Public decklist-submission form, if the game accepts submissions. */
  submitFormUrl: "https://forms.gle/52BTV7suZ7bFqDLQ9",

  // ============================================================================
  // NAVIGATION & UI
  // ============================================================================
  navigation: {
    /** The game's deck builder, or undefined if it has none yet. */
    deckbuilderHref: "/deckbuilder?layout=edit" as string | undefined,
    showMarketWatch: true,
    marketWatchHref: "/market-watch",
    showTools: true,
    showBoosters: true,
    showStarters: true,
    showSearch: true,
  },

  // ============================================================================
  // DOMAIN CONFIGURATION
  // ============================================================================
  domain: {
    ...GAME_DOMAINS["one-piece"],
    disabledRoutes: [] as string[],
  },

  // ============================================================================
  // GAME HANDLER & DECK BUILDER
  // ============================================================================
  handler: () => new OPHandler(),

  deckBuilder: {
    getCardData,
    getAACardIdData,
    getCardImage,
    getAAFromCardId,
  },

  // ============================================================================
  // CDN CONFIGURATION
  // ============================================================================
  cdn: {
    leaderSquaresPath: `${CDN}/games/one-piece/images/leader-squares`,
  },

  // ============================================================================
  // INITIAL STATE FOR STORES (auto-generated, no need to modify)
  // ============================================================================
  initialState: {
    prices: null,
    loading: false,
    decks: [],
    cardOrderIds: [],
    filters: {},
  },
};

export type OnePieceGame = typeof onePieceGame;
